import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Save, ArrowLeft, Copy, X } from 'lucide-react';
import { cn, getWeekStart, getDayName, getDayPartLabel } from '../lib/utils';
import { toast } from 'sonner';

const SHORT_LABELS = { MORNING: 'AM', AFTERNOON: 'PM', EVENING: 'EVE' };

function getNextWeekStart(weekStart) {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 7);
  return d.toISOString().split('T')[0];
}

export default function AvailabilityPage({ getAvailability, updateAvailability, backTo }) {
  const [weekStart, setWeekStart] = useState(getWeekStart());
  const [allSlots, setAllSlots] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAvailability(); }, []);

  useEffect(() => {
    setSlots(allSlots.filter((s) => s.weekStart === weekStart));
    setIsDirty(false);
  }, [weekStart, allSlots]);

  const loadAvailability = async () => {
    try {
      const response = await getAvailability();
      setAllSlots(response.data);
    } catch (error) {
      console.error('Failed to load availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAvailability({
        weekStart,
        slots: slots.map((s) => ({ dayOfWeek: s.dayOfWeek, dayPart: s.dayPart })),
      });
      setAllSlots((prev) => [
        ...prev.filter((s) => s.weekStart !== weekStart),
        ...slots,
      ]);
      setIsDirty(false);
      toast.success('Availability saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToNextWeek = async () => {
    if (slots.length === 0) { toast.error('No slots to copy — select some first'); return; }
    setCopying(true);
    const next = getNextWeekStart(weekStart);
    try {
      await updateAvailability({
        weekStart: next,
        slots: slots.map((s) => ({ dayOfWeek: s.dayOfWeek, dayPart: s.dayPart })),
      });
      const nextSlots = slots.map((s) => ({ ...s, weekStart: next }));
      setAllSlots((prev) => [...prev.filter((s) => s.weekStart !== next), ...nextSlots]);
      toast.success('Copied to next week');
      setWeekStart(next);
    } catch (error) {
      toast.error('Failed to copy');
    } finally {
      setCopying(false);
    }
  };

  const handleClearWeek = () => {
    if (slots.length === 0) return;
    setSlots([]);
    setIsDirty(true);
  };

  const navigateWeek = (direction) => {
    if (isDirty) toast.warning("Unsaved changes — save before switching weeks or they'll be lost");
    const current = new Date(weekStart);
    current.setDate(current.getDate() + direction * 7);
    setWeekStart(current.toISOString().split('T')[0]);
  };

  const toggleSlot = (dayOfWeek, dayPart) => {
    const exists = slots.find((s) => s.dayOfWeek === dayOfWeek && s.dayPart === dayPart);
    if (exists) setSlots(slots.filter((s) => !(s.dayOfWeek === dayOfWeek && s.dayPart === dayPart)));
    else setSlots([...slots, { dayOfWeek, dayPart, weekStart }]);
    setIsDirty(true);
  };

  const applyQuickSelect = (type) => {
    const newSlots = type === 'evenings'
      ? [1,2,3,4,5,6,7].map((d) => ({ dayOfWeek: d, dayPart: 'EVENING', weekStart }))
      : [6,7].flatMap((d) => ['MORNING','AFTERNOON','EVENING'].map((p) => ({ dayOfWeek: d, dayPart: p, weekStart })));
    const merged = [...slots];
    for (const ns of newSlots) {
      if (!merged.find((s) => s.dayOfWeek === ns.dayOfWeek && s.dayPart === ns.dayPart)) merged.push(ns);
    }
    setSlots(merged);
    setIsDirty(true);
  };

  const isSlotSelected = (dayOfWeek, dayPart) =>
    slots.some((s) => s.dayOfWeek === dayOfWeek && s.dayPart === dayPart);

  const getWeekDates = () => {
    const start = new Date(weekStart);
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(start); date.setDate(start.getDate() + i); return date;
    });
  };

  const todayWeekStart = getWeekStart();
  const futureWeeksWithSlots = new Set(
    allSlots.filter((s) => s.weekStart > todayWeekStart).map((s) => s.weekStart)
  ).size;
  const isPastWeek = weekStart < todayWeekStart;
  const dayParts = ['MORNING', 'AFTERNOON', 'EVENING'];
  const activeSlotCount = slots.length;
  const today = new Date(); today.setHours(0,0,0,0);

  if (loading) {
    return (
      <div className="page-container-lg">
        <div className="h-64 bg-[#1e1915] animate-pulse rounded-xl" />
      </div>
    );
  }

  return (
    <div className="page-container-lg">
      <div className="mb-5">
        <Link to={backTo} className="inline-flex items-center gap-1 text-white/40 hover:text-white/60 text-sm mb-3 transition-colors" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" />Back
        </Link>
        <h1 className="section-header text-2xl text-white">My Availability</h1>
        <p className="text-white/40 mt-1 text-xs">Tap slots when you're free</p>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-3 rounded-xl bg-gradient-to-br from-[#1e1915] to-[#171411] border border-white/10 px-3 py-3">
        <button onClick={() => navigateWeek(-1)} className="p-2 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white/50 transition-colors" data-testid="prev-week-btn">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="text-center">
          <span className="text-sm font-medium text-white">
            Week of {new Date(weekStart).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
          </span>
          {isPastWeek && <div className="text-[10px] text-white/30 uppercase tracking-wider mt-0.5">Past</div>}
        </div>
        <div className="flex items-center gap-1">
          {slots.length > 0 && (
            <button onClick={handleClearWeek} className="p-2 rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-red-500/10 hover:border-red-500/30 text-white/30 hover:text-red-400 transition-all" title="Clear this week" data-testid="clear-week-btn">
              <X className="h-4 w-4" />
            </button>
          )}
          <button onClick={() => navigateWeek(1)} className="p-2 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white/50 transition-colors" data-testid="next-week-btn">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Quick-select chips */}
      <div className="flex gap-2 mb-3">
        {[['evenings','All Evenings'],['weekends','Full Weekend']].map(([type, label]) => (
          <button key={type} onClick={() => applyQuickSelect(type)}
            className="px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] hover:bg-[color-mix(in_srgb,var(--s8-amber)_10%,transparent)] hover:border-[color-mix(in_srgb,var(--s8-amber)_30%,transparent)] text-white/40 hover:text-[var(--s8-amber)] text-xs font-medium transition-all"
            data-testid={`quick-${type}-btn`}>
            {label}
          </button>
        ))}
      </div>

      {/* Desktop Grid */}
      <div className="hidden md:block rounded-xl bg-gradient-to-br from-[#1e1915] to-[#171411] border border-white/10 p-4">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="p-2 text-left text-xs text-white/40 uppercase tracking-wider font-medium w-24" />
              {getWeekDates().map((date, i) => {
                const isPast = date < today;
                return (
                  <th key={date.toISOString()} className={cn('p-2 text-center', isPast && 'opacity-35')}>
                    <div className="text-[10px] text-white/40 uppercase tracking-wider">{getDayName(i + 1)}</div>
                    <div className="font-semibold text-white text-sm mt-0.5">{date.getDate()}</div>
                    {isPast && <div className="text-[9px] text-white/20 uppercase tracking-wider">past</div>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {dayParts.map((part) => (
              <tr key={part}>
                <td className="p-2 text-xs text-white/40 uppercase tracking-wider font-medium">{getDayPartLabel(part)}</td>
                {[1,2,3,4,5,6,7].map((day) => {
                  const date = getWeekDates()[day - 1];
                  const isPast = date < today;
                  const selected = isSlotSelected(day, part);
                  return (
                    <td key={day} className={cn('p-1', isPast && 'opacity-35')}>
                      <button onClick={() => !isPast && toggleSlot(day, part)} disabled={isPast}
                        className={cn('w-full h-11 rounded-lg border transition-all duration-150 ease-out select-none flex items-center justify-center',
                          isPast ? 'cursor-not-allowed' : 'active:scale-[0.96]',
                          selected ? 'bg-[color-mix(in_srgb,var(--s8-amber)_15%,transparent)] border-[color-mix(in_srgb,var(--s8-amber)_50%,transparent)] text-[var(--s8-amber)]' : 'bg-white/[0.02] border-white/[0.08] hover:bg-white/[0.05] text-white/30'
                        )}
                        data-testid={`slot-${day}-${part}`}>
                        {selected && <span className="text-xs font-bold">&#x2713;</span>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="md:hidden rounded-xl bg-gradient-to-br from-[#1e1915] to-[#171411] border border-white/10 overflow-hidden">
        <div className="grid grid-cols-[56px_1fr_1fr_1fr] gap-1.5 px-3 pt-3 pb-1.5">
          <div />
          {dayParts.map((part) => (
            <div key={part} className="text-center text-[10px] text-white/30 uppercase tracking-wider font-medium">{SHORT_LABELS[part]}</div>
          ))}
        </div>
        <div className="px-3 pb-3 space-y-1.5">
          {getWeekDates().map((date, i) => {
            const isPast = date < today;
            return (
              <div key={date.toISOString()} className={cn('grid grid-cols-[56px_1fr_1fr_1fr] gap-1.5 items-center', isPast && 'opacity-35')}>
                <div className="pr-1">
                  <div className="text-[10px] text-white/40 uppercase tracking-wider leading-none">{getDayName(i + 1)}</div>
                  <div className="text-white font-semibold text-sm leading-tight">{date.getDate()}</div>
                  {isPast && <div className="text-[9px] text-white/20 uppercase leading-tight">past</div>}
                </div>
                {dayParts.map((part) => {
                  const selected = isSlotSelected(i + 1, part);
                  return (
                    <button key={part} onClick={() => !isPast && toggleSlot(i + 1, part)} disabled={isPast}
                      className={cn('py-2.5 rounded-lg border text-xs font-medium uppercase tracking-wide transition-all duration-150 ease-out select-none',
                        isPast ? 'cursor-not-allowed' : 'active:scale-[0.94]',
                        selected ? 'bg-[color-mix(in_srgb,var(--s8-amber)_15%,transparent)] border-[color-mix(in_srgb,var(--s8-amber)_50%,transparent)] text-[var(--s8-amber)]' : 'bg-white/[0.02] border-white/[0.08] text-white/30'
                      )}
                      data-testid={`mobile-slot-${i + 1}-${part}`}>
                      {SHORT_LABELS[part]}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className="px-3 py-2.5 border-t border-white/[0.06]">
          <div className="flex items-center justify-between text-xs text-white/40">
            <div className="flex items-center gap-2">
              <span className={cn('w-1.5 h-1.5 rounded-full', activeSlotCount > 0 ? 'bg-[var(--s8-amber)]' : 'bg-white/10')} />
              {activeSlotCount} slot{activeSlotCount !== 1 ? 's' : ''} this week
              {isDirty && <span className="text-yellow-500/60">• unsaved</span>}
            </div>
            {futureWeeksWithSlots > 0 && (
              <span className="text-white/25">{futureWeeksWithSlots}w ahead set</span>
            )}
          </div>
        </div>
      </div>

      {/* Desktop status line */}
      <div className="hidden md:flex items-center justify-between mt-2 px-1 text-xs text-white/30">
        <div className="flex items-center gap-2">
          <span className={cn('w-1.5 h-1.5 rounded-full', activeSlotCount > 0 ? 'bg-[var(--s8-amber)]' : 'bg-white/10')} />
          {activeSlotCount} slot{activeSlotCount !== 1 ? 's' : ''} this week
          {isDirty && <span className="text-yellow-500/60">• unsaved</span>}
        </div>
        {futureWeeksWithSlots > 0 && (
          <span>{futureWeeksWithSlots} week{futureWeeksWithSlots !== 1 ? 's' : ''} ahead set</span>
        )}
      </div>

      {/* Action buttons */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Button onClick={handleCopyToNextWeek} disabled={copying || slots.length === 0} variant="outline"
          className="h-12 border-white/15 bg-white/[0.03] text-white/70 hover:bg-white/[0.07] hover:text-white disabled:opacity-30 font-semibold"
          data-testid="copy-next-week-btn">
          <Copy className="h-4 w-4 mr-2" />
          {copying ? 'Copying...' : 'Copy to Next Week'}
        </Button>
        <Button onClick={handleSave} disabled={saving}
          className={cn('h-12 text-base font-bold uppercase tracking-wide bg-[var(--s8-amber)] text-[#0c0a08] hover:bg-[var(--s8-amber)]', isDirty && 'ring-2 ring-[color-mix(in_srgb,var(--s8-amber)_40%,transparent)]')}
          data-testid="save-availability-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

